import React, {useEffect, useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";
import {Autoplay, Navigation, Pagination} from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";

import {ReactComponent as ProjectIcon} from "../../../assets/svg/ProjectIcon.svg";
import OtherPortfolioDetails from "./OtherPortfolio";
import {getCMSGeneral} from "../../../utils/cms.utils";
import {Link} from "react-router-dom";

const PortfolioDetailsMain = ({dataList, singleData, services}) => {
    const [general, setGeneral] = useState(null);

    useEffect(() => {
        setGeneral(getCMSGeneral());
    }, []);

    const serviceMap = React.useMemo(() => {
        return new Map(
            services.map((s) => [String(s.id), s])
        );
    }, [services]);

    if (!singleData || !Array.isArray(dataList)) return null;

    const usedServices = Array.isArray(singleData.tags)
        ? singleData.tags
            .map((id) => serviceMap.get(String(id)))
            .filter(Boolean)
        : [];

    const overviews = Array.isArray(singleData.overviews)
        ? [...singleData.overviews].sort(
            (a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0)
        )
        : [];

    const descriptions = Array.isArray(singleData.descriptions)
        ? [...singleData.descriptions].sort(
            (a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0)
        )
        : [];

    const goals = Array.isArray(singleData.goals)
        ? [...singleData.goals].sort(
            (a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0)
        )
        : [];

    return (
        <section className="portfolio-details section-padding">
            <div className="container">
                <div className="row portfolio-details__layout">
                    {/* LEFT – OVERVIEW */}
                    <div className="col-lg-4 portfolio-details__left">
                        <div className="portfolio-details__overview">
                            <h4 className="portfolio-details__overview-title">
                                <span className="portfolio-details__overview-icon">
                                    <ProjectIcon/>
                                </span>
                                Project Overview
                            </h4>

                            <div className="portfolio-details__overview-list">
                                {overviews.map((item, index) => (
                                    <div
                                        className="portfolio-details__overview-row"
                                        key={index}
                                    >
                                        <div className="overview-label">
                                            <span className="en">{item.labelEn}</span>
                                            {item.labelId && (
                                                <span className="id">
                                                    ({item.labelId})
                                                </span>
                                            )}
                                        </div>

                                        <div className="overview-value">
                                            {item.value}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* SERVICE OVERVIEW */}
                        {usedServices.length > 0 && (
                            <div className="portfolio-details__overview mt-4">
                                <h4 className="portfolio-details__overview-title">
            <span className="portfolio-details__overview-icon">
                <i className="flaticon-settings" />
            </span>
                                    Services Overview
                                </h4>

                                <div className="portfolio-details__services">
                                    {usedServices.map((service) => (
                                        <Link
                                            key={service.id}
                                            to={`/services/${service.id}`}
                                            className="portfolio-details__service-item"
                                        >
                                            <i className="flaticon-check-mark" />
                                            <span>{service.titleEn}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* OTHER PROJECTS – DESKTOP */}
                        <div className="portfolio-details__other desktop-only">
                            <OtherPortfolioDetails
                                dataList={dataList}
                                id={singleData.id}
                            />
                        </div>
                    </div>

                    {/* RIGHT – CONTENT */}
                    <div className="col-lg-8 portfolio-details__right">
                        <div className="portfolio-details__content">
                            <h3 className="portfolio-details__title">
                                {singleData.title}
                            </h3>

                            {/* LOGO */}
                            <div className="portfolio-details__logos">
                                <img src={general?.logo1} alt="RAR Logo"/>

                                {singleData.logo && (
                                    <>
                                        <span className="multiply">×</span>
                                        <img
                                            src={singleData.logo}
                                            alt="Client Logo"
                                            className="client-logo"
                                        />
                                    </>
                                )}
                            </div>

                            {/* GALLERY */}
                            {singleData.images?.length > 0 && (
                                <div className="portfolio-details__gallery">
                                    <Swiper
                                        modules={[
                                            Pagination,
                                            Autoplay,
                                            Navigation,
                                        ]}
                                        pagination={{clickable: true}}
                                        navigation
                                        autoplay={{delay: 4000}}
                                        spaceBetween={20}
                                        slidesPerView={1}
                                    >
                                        {singleData.images.map((img, index) => (
                                            <SwiperSlide key={index}>
                                                <div className="portfolio-details__slide">
                                                    <img
                                                        src={img}
                                                        alt={`slide-${index}`}
                                                    />
                                                </div>
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {/* CAPTION */}
                            {singleData.caption && (
                                <h5 className="portfolio-details__caption">
                                    {singleData.caption}
                                </h5>
                            )}

                            {/* DESCRIPTION */}
                            {descriptions.length > 0 && (
                                <div className="portfolio-details__description">
                                    {descriptions.map((desc, index) => (
                                        <div key={index} className="description-block">
                                            {/* EN */}
                                            <p
                                                className="desc-main"
                                                dangerouslySetInnerHTML={{
                                                    __html: desc.descEn,
                                                }}
                                            />

                                            {/* DIVIDER */}
                                            {desc.descId && <div className="divider"/>}

                                            {/* ID */}
                                            {desc.descId && (
                                                <p
                                                    className="desc-sub"
                                                    dangerouslySetInnerHTML={{
                                                        __html: desc.descId,
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}


                            {/* GOALS */}
                            {goals.length > 0 && (
                                <div className="portfolio-details__goals">
                                    <h4>Project Goals</h4>

                                    <ul>
                                        {goals.map((goal, index) => (
                                            <li key={index}>
                                                <span className="goal-icon">
                                                    <i className="flaticon-check-mark"/>
                                                </span>

                                                <div>
                                                    <div
                                                        className="goal-en"
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                            goal.goalsEn,
                                                        }}
                                                    />
                                                    {goal.goalsId && (
                                                        <div
                                                            className="goal-id"
                                                            dangerouslySetInnerHTML={{
                                                                __html:
                                                                goal.goalsId,
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* OTHER PROJECTS — MOBILE */}
            <div className="mobile-only mt-4">
                <OtherPortfolioDetails
                    dataList={dataList}
                    services={services}
                    id={singleData.id}
                />
            </div>
        </section>
    );
};

export default PortfolioDetailsMain;
